import { useCallback } from 'react';
import type { Property, RenderVariant, RenderSettings } from '../../types/property';

interface SelectedPhotoData {
  roomName: string;
  variants: RenderVariant[];
  originalUrl?: string;
  renderSettings?: RenderSettings;
  originalGcsPath?: string;
  generationId?: string;
}

interface UsePhotoClickParams {
  properties: Property[];
  setSelectedProperty: (p: Property | null) => void;
  setSelectedPhotoData: (data: SelectedPhotoData | null) => void;
  setIsRenderPreviewOpen: (open: boolean) => void;
}

export function usePhotoClick({
  properties,
  setSelectedProperty,
  setSelectedPhotoData,
  setIsRenderPreviewOpen,
}: UsePhotoClickParams) {

  // For AllPhotosView and Favorites — takes a Photo object with propertyAddress
  const handleAllPhotosClick = useCallback((photo: { url: string; propertyAddress: string }) => {
    const property = properties.find(p => p.address === photo.propertyAddress);
    if (!property) return;
    const imageObj = property.galleryImages?.find(img => img.url === photo.url);
    if (!imageObj) return;
    setSelectedProperty(property);
    setSelectedPhotoData({
      roomName: imageObj.roomName || 'Pokój',
      variants: imageObj.variants || [],
      originalUrl: imageObj.originalUrl,
      renderSettings: (imageObj as any).renderSettings,
      generationId: imageObj.generationId,
    });
    setIsRenderPreviewOpen(true);
  }, [properties, setSelectedProperty, setSelectedPhotoData, setIsRenderPreviewOpen]);

  // For PropertyDetail — takes direct variant data from component params
  const handlePropertyPhotoClick = useCallback((
    photoUrl: string,
    photoIndex: number,
    _variantCount: number | undefined,
    roomName: string | undefined,
    variants: RenderVariant[] | undefined,
    originalUrl: string | undefined,
    generationId?: string,
  ) => {
    const photoVariants = variants && variants.length > 0
      ? variants
      : [];
    setSelectedPhotoData({
      roomName: roomName || 'Pokój',
      variants: photoVariants,
      originalUrl: originalUrl || photoUrl,
      generationId,
    });
    setIsRenderPreviewOpen(true);
  }, [setSelectedPhotoData, setIsRenderPreviewOpen]);

  return { handleAllPhotosClick, handlePropertyPhotoClick };
}
